/**
 * The mail server fields, shared by the platform's form and each school's.
 *
 * Only the fields. The switch above them differs by scope - the platform has
 * "send email notifications", a school has "use our own mail server" - and so
 * does what saving means, so those stay with their owners rather than being
 * bent into one component with a mode flag.
 *
 * The password is never prefilled, because the server never sends it back.
 * Left blank it means "keep what is saved", which is why the request omits the
 * field rather than sending an empty string. Clearing one is a separate,
 * deliberate checkbox.
 */

import { useState } from 'react'
import { Button, Checkbox, Field, Input, PasswordInput } from '../../components/ui'
import { useAuth } from '../../app/useAuth'

export type SmtpDraft = {
  smtp_host: string
  smtp_port: string
  smtp_username: string
  smtp_password: string
  smtp_starttls: boolean
  smtp_from_email: string
  smtp_from_name: string
  smtp_reply_to: string
}

export function draftFrom(saved: {
  smtp_host: string
  smtp_port: number
  smtp_username: string
  smtp_starttls: boolean
  smtp_from_email: string
  smtp_from_name: string
  smtp_reply_to: string
}): SmtpDraft {
  return {
    smtp_host: saved.smtp_host,
    smtp_port: String(saved.smtp_port),
    smtp_username: saved.smtp_username,
    smtp_password: '',
    smtp_starttls: saved.smtp_starttls,
    smtp_from_email: saved.smtp_from_email,
    smtp_from_name: saved.smtp_from_name,
    smtp_reply_to: saved.smtp_reply_to,
  }
}

export function portOf(draft: SmtpDraft) {
  const port = Number(draft.smtp_port)
  return { port, valid: Number.isInteger(port) && port >= 1 && port <= 65535 }
}

export function SmtpFields({
  value,
  set,
  passwordConfigured,
  clearPassword,
  onClearPassword,
  disabled = false,
}: {
  value: SmtpDraft
  set: <K extends keyof SmtpDraft>(key: K, next: SmtpDraft[K]) => void
  passwordConfigured: boolean
  clearPassword: boolean
  onClearPassword: (next: boolean) => void
  disabled?: boolean
}) {
  const { valid: portValid } = portOf(value)

  return (
    <>
      <div className="tc-email-grid">
        <Field label="SMTP server" hint="For example smtp.gmail.com or smtp.office365.com.">
          <Input
            value={value.smtp_host}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => set('smtp_host', e.target.value)}
          />
        </Field>
        <Field
          label="Port"
          hint="465 encrypts from the start. 587 needs STARTTLS below. 25 is unencrypted."
          error={portValid ? undefined : 'Enter a port between 1 and 65535.'}
        >
          <Input
            type="number"
            min={1}
            max={65535}
            value={value.smtp_port}
            disabled={disabled}
            onChange={(e) => set('smtp_port', e.target.value)}
          />
        </Field>
        <Field label="Username" hint="Leave blank if the server does not need a sign-in.">
          <Input
            value={value.smtp_username}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => set('smtp_username', e.target.value)}
          />
        </Field>
        <Field
          label="Password"
          hint={
            passwordConfigured
              ? 'A password is saved. Leave blank to keep it.'
              : 'No password saved yet.'
          }
        >
          <PasswordInput
            value={value.smtp_password}
            disabled={disabled}
            autoComplete="new-password"
            placeholder={passwordConfigured ? '••••••••' : ''}
            onChange={(e) => set('smtp_password', e.target.value)}
          />
        </Field>
      </div>

      {passwordConfigured && (
        <Checkbox
          label="Remove the saved password"
          checked={clearPassword}
          disabled={disabled || value.smtp_password.length > 0}
          onChange={(e) => onClearPassword(e.target.checked)}
        />
      )}

      {/* Port 465 is encrypted before any SMTP is spoken, so STARTTLS does not
          apply and the server picks it from the port. Showing the switch there
          would offer a choice that has no effect. */}
      {portOf(value).port === 465 ? (
        <p className="tc-email-note">
          Port 465 is encrypted from the start, so STARTTLS does not apply.
        </p>
      ) : (
        <Checkbox
          label="Use STARTTLS"
          checked={value.smtp_starttls}
          disabled={disabled}
          onChange={(e) => set('smtp_starttls', e.target.checked)}
        />
      )}

      <div className="tc-email-grid">
        <Field label="From address" hint="What recipients see the message came from.">
          <Input
            type="email"
            value={value.smtp_from_email}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => set('smtp_from_email', e.target.value)}
          />
        </Field>
        <Field label="From name" hint="Optional. Shown beside the address.">
          <Input
            value={value.smtp_from_name}
            disabled={disabled}
            onChange={(e) => set('smtp_from_name', e.target.value)}
          />
        </Field>
        <Field
          label="Reply-to address"
          hint="Optional. Where replies go, if that is not the from address."
        >
          <Input
            type="email"
            value={value.smtp_reply_to}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => set('smtp_reply_to', e.target.value)}
          />
        </Field>
      </div>
    </>
  )
}

/**
 * "Send a test to this address", shared by both scopes.
 *
 * The address defaults to the signed-in administrator and is usually left
 * alone. It can be changed because the question a test actually answers is
 * "does mail from this server reach a real inbox" - and a relay's own domain
 * cannot answer that. Checking whether Gmail accepts it, or files it as spam,
 * needs a Gmail address.
 *
 * Disabled while the form is dirty: testing settings you have not saved reports
 * on the old ones and is worse than not testing at all.
 */
export function TestSend({
  dirty,
  pending,
  onSend,
}: {
  dirty: boolean
  pending: boolean
  onSend: (to: string) => void
}) {
  const { user } = useAuth()
  const [to, setTo] = useState('')

  return (
    <>
      <div className="tc-email-grid">
        <Field
          label="Send test to"
          hint={`Leave blank to use your own address, ${user?.email ?? ''}.`}
        >
          <Input
            type="email"
            value={to}
            placeholder={user?.email ?? ''}
            autoComplete="off"
            onChange={(event) => setTo(event.target.value)}
          />
        </Field>
      </div>
      <div className="tc-email-actions">
        <Button
          variant="ghost"
          disabled={dirty || pending}
          onClick={() => onSend(to)}
          title={dirty ? 'Save your changes before testing.' : undefined}
        >
          {pending ? 'Sending…' : 'Send test email'}
        </Button>
      </div>
    </>
  )
}
